/**
 * LexoRank utility for calculating ranks between items.
 * Based on base-62 alphabet with fixed length of 15 characters.
 */

const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const RANK_LENGTH = 15;

const charToInt = (c: string): number => ALPHABET.indexOf(c);
const intToChar = (i: number): string => ALPHABET[i];

/**
 * Calculates a rank string that falls between prevRank and nextRank.
 * Uses lexicographic ordering with base-62 alphabet.
 *
 * @param prevRank - The rank of the item before the target position (null if inserting at start)
 * @param nextRank - The rank of the item after the target position (null if inserting at end)
 * @returns A new rank string that falls between prevRank and nextRank
 */
export const calculateRankBetween = (prevRank: string | null, nextRank: string | null): string => {
  const minRank = '0'.repeat(RANK_LENGTH);
  const maxRank = 'z'.repeat(RANK_LENGTH);

  const a = (prevRank || minRank).padEnd(RANK_LENGTH, '0');
  const b = (nextRank || maxRank).padEnd(RANK_LENGTH, 'z');

  const out: string[] = [];

  for (let i = 0; i < RANK_LENGTH; i += 1) {
    const da = charToInt(a[i]);
    const db = charToInt(b[i]);

    if (da === db) {
      out.push(intToChar(da));
    } else if (db - da > 1) {
      const mid = Math.floor((da + db) / 2);
      out.push(intToChar(mid));
      // Fill rest with '0'
      for (let j = i + 1; j < RANK_LENGTH; j += 1) {
        out.push('0');
      }
      return out.join('');
    } else {
      // Adjacent (db === da + 1) - continue to next position
      out.push(intToChar(da));
    }
  }

  return out.join('');
};
